import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Users, Send, ArrowLeft, RefreshCw, User, Clock, 
  CheckCircle, Heart, Leaf, Eye, MessageCircle, Sparkles,
  Play, UserPlus, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { SpiralIcon, FlameIcon, HeartAlchemyIcon, LotusIcon, CrystalIcon } from './icons/AlchemyIcons';
import PremiumFooter from './PremiumFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Exercise type configurations
const EXERCISE_TYPES = {
  ofnr: {
    name: 'OFNR Practice',
    description: 'Practice Observation, Feeling, Need, Request',
    icon: SpiralIcon,
    color: 'amber'
  },
  empathy: {
    name: 'Empathy Practice',
    description: 'Practice listening and reflecting back',
    icon: HeartAlchemyIcon,
    color: 'rose'
  },
  self_empathy: {
    name: 'Self-Empathy',
    description: 'Connect with your own feelings and needs',
    icon: LotusIcon,
    color: 'teal'
  }
};

// User avatar colors for differentiation
const USER_COLORS = [
  'bg-amber-500', 'bg-rose-500', 'bg-teal-500', 'bg-violet-500', 
  'bg-blue-500', 'bg-emerald-500', 'bg-orange-500', 'bg-pink-500'
];

function getUserColor(userId) {
  // Generate consistent color based on user ID
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

export default function GroupPractice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  
  const [userId, setUserId] = useState(() => localStorage.getItem('workbook_user_id'));
  const [userName, setUserName] = useState(() => localStorage.getItem('workbook_user_name') || 'Anonymous');
  
  const [session, setSession] = useState(null);
  const [fieldInputs, setFieldInputs] = useState({});
  const [allInputs, setAllInputs] = useState([]);
  const [userView, setUserView] = useState(null); // For compiled by-user view
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [activeTab, setActiveTab] = useState('practice');
  const [expandedUsers, setExpandedUsers] = useState({});
  
  const lastFetchTime = useRef('');
  const pollInterval = useRef(null);

  // Check auth with retry
  useEffect(() => {
    const checkAuth = () => {
      const currentUserId = localStorage.getItem('workbook_user_id');
      const currentUserName = localStorage.getItem('workbook_user_name');
      
      if (!currentUserId) {
        // Give a brief moment for any async session restoration
        setTimeout(() => {
          const retryUserId = localStorage.getItem('workbook_user_id');
          if (!retryUserId) {
            toast.error('Please log in to join group practice');
            navigate('/login');
          } else {
            setUserId(retryUserId);
            setUserName(localStorage.getItem('workbook_user_name') || 'Anonymous');
          }
        }, 150);
        return;
      }
      
      setUserId(currentUserId);
      setUserName(currentUserName || 'Anonymous');
    };
    
    checkAuth();
  }, [navigate]);

  // Fetch session data
  useEffect(() => {
    if (sessionId) {
      fetchSession();
      // Poll for updates every 2 seconds
      pollInterval.current = setInterval(fetchUpdates, 2000);
      
      return () => {
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }
      };
    }
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/group-practice/sessions/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setParticipants(data.participants || []);
        
        // Initialize field inputs
        const fields = {};
        data.practice_fields?.forEach(field => {
          fields[field.name] = '';
        });
        setFieldInputs(fields);
        
        // Process existing inputs
        setAllInputs(data.field_inputs || []);
        if (data.field_inputs?.length > 0) {
          lastFetchTime.current = data.field_inputs[data.field_inputs.length - 1].created_at;
        }
        
        // Join session if not already a participant
        await joinSession();
      } else {
        toast.error('Session not found');
        navigate('/forum');
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const joinSession = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/group-practice/sessions/${sessionId}/join`, {
        method: 'POST',
        headers: {
          'X-User-Id': userId,
          'X-User-Name': userName
        }
      });
    } catch (error) {
      console.error('Failed to join session:', error);
    }
  };

  const fetchUpdates = async () => {
    try {
      const url = `${BACKEND_URL}/api/group-practice/sessions/${sessionId}/inputs${lastFetchTime.current ? `?since=${lastFetchTime.current}` : ''}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.inputs?.length > 0) {
          setAllInputs(prev => [...prev, ...data.inputs]);
          lastFetchTime.current = data.inputs[data.inputs.length - 1].created_at;
        }
        setParticipants(data.participants || []);
      }
    } catch (error) {
      console.error('Failed to fetch updates:', error);
    }
  };

  const fetchByUserView = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/group-practice/sessions/${sessionId}/by-user`);
      if (response.ok) {
        const data = await response.json();
        setUserView(data);
      }
    } catch (error) {
      console.error('Failed to fetch user view:', error);
    }
  };

  const submitFieldInput = async (fieldName, fieldLabel) => {
    const content = fieldInputs[fieldName];
    if (!content?.trim()) {
      toast.error('Please enter something first');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(`${BACKEND_URL}/api/group-practice/sessions/${sessionId}/input`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({
          session_id: sessionId,
          field_name: fieldName,
          field_label: fieldLabel,
          content: content.trim(),
          field_type: 'text'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAllInputs(prev => [...prev, data.input]);
        setFieldInputs(prev => ({ ...prev, [fieldName]: '' }));
        toast.success('Shared with the group!');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to share');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const toggleUserExpanded = (uid) => {
    setExpandedUsers(prev => ({
      ...prev,
      [uid]: !prev[uid]
    }));
  };

  // Get inputs for a specific field
  const getFieldInputs = (fieldName) => {
    return allInputs.filter(inp => inp.field_name === fieldName);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-stone-400">Loading group practice session...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-400">Session not found</p>
          <Button onClick={() => navigate('/forum')} className="mt-4">
            Back to Forum
          </Button>
        </div>
      </div>
    );
  }

  const exerciseType = EXERCISE_TYPES[session.exercise_type] || EXERCISE_TYPES.ofnr;
  const ExerciseIcon = exerciseType.icon;

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-900/95 backdrop-blur-sm border-b border-amber-500/20">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/forum')}
                className="text-stone-400 hover:text-white"
                data-testid="back-to-forum-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Forum
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <ExerciseIcon className="w-5 h-5 text-amber-400" />
                  <h1 className="text-lg font-medium text-white">{session.title}</h1>
                </div>
                <p className="text-xs text-stone-500">{exerciseType.description}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                <Users className="w-3 h-3 mr-1" />
                {participants.length} active
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchByUserView}
                className="border-stone-700 text-stone-400 hover:text-white"
                data-testid="view-by-user-btn"
              >
                <Eye className="w-4 h-4 mr-1" />
                By User
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Participants Bar */}
        <div className="mb-6 p-4 bg-stone-900/50 border border-stone-800 rounded-lg">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-stone-500 mr-2">Participants:</span>
            {participants.map((p, i) => (
              <div 
                key={p.user_id} 
                className="flex items-center gap-1"
                title={`${p.user_name} joined ${formatTime(p.joined_at)}`}
              >
                <div className={`w-6 h-6 rounded-full ${getUserColor(p.user_id)} flex items-center justify-center text-white text-xs font-medium`}>
                  {p.user_name.charAt(0).toUpperCase()}
                </div>
                <span className={`text-xs ${p.user_id === userId ? 'text-amber-400 font-medium' : 'text-stone-400'}`}>
                  {p.user_id === userId ? 'You' : p.user_name}
                </span>
              </div>
            ))}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-stone-800/50 border border-stone-700">
            <TabsTrigger value="practice" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <SpiralIcon className="w-4 h-4 mr-2" />
              Practice
            </TabsTrigger>
            <TabsTrigger value="stream" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <MessageCircle className="w-4 h-4 mr-2" />
              Live Stream
            </TabsTrigger>
            <TabsTrigger value="compiled" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              By User
            </TabsTrigger>
          </TabsList>

          {/* Practice Tab - Input Fields */}
          <TabsContent value="practice" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {session.practice_fields?.map((field, index) => (
                <Card 
                  key={field.name} 
                  className="bg-stone-900/50 border-stone-800"
                  data-testid={`practice-field-${field.name}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium text-amber-300">
                        {field.label}
                      </CardTitle>
                      <Badge variant="outline" className="text-xs border-stone-700 text-stone-500">
                        {getFieldInputs(field.name).length} responses
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {field.type === 'multiline' ? (
                      <Textarea
                        value={fieldInputs[field.name] || ''}
                        onChange={(e) => setFieldInputs(prev => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder={`Share your ${field.label.toLowerCase()}...`}
                        className="bg-stone-800/50 border-stone-700 text-white min-h-[100px] resize-none"
                        data-testid={`input-${field.name}`}
                      />
                    ) : (
                      <Input
                        value={fieldInputs[field.name] || ''}
                        onChange={(e) => setFieldInputs(prev => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder={`Enter your ${field.label.toLowerCase()}...`}
                        className="bg-stone-800/50 border-stone-700 text-white"
                        data-testid={`input-${field.name}`}
                      />
                    )}
                    <Button
                      onClick={() => submitFieldInput(field.name, field.label)}
                      disabled={!fieldInputs[field.name]?.trim() || submitting[field.name]}
                      className="w-full bg-amber-600 hover:bg-amber-500 text-white"
                      data-testid={`submit-${field.name}`}
                    >
                      {submitting[field.name] ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Share with Group
                    </Button>
                    
                    {/* Show recent inputs for this field */}
                    {getFieldInputs(field.name).length > 0 && (
                      <div className="mt-4 space-y-2 max-h-[200px] overflow-y-auto">
                        {getFieldInputs(field.name).slice(-5).map((inp) => (
                          <div 
                            key={inp._id}
                            className={`p-2 rounded text-sm ${
                              inp.user_id === userId 
                                ? 'bg-amber-500/10 border border-amber-500/20' 
                                : 'bg-stone-800/50 border border-stone-700'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-4 h-4 rounded-full ${getUserColor(inp.user_id)} flex items-center justify-center text-white text-[10px]`}>
                                {inp.user_name.charAt(0)}
                              </div>
                              <span className={`text-xs font-medium ${inp.user_id === userId ? 'text-amber-400' : 'text-teal-400'}`}>
                                {inp.user_id === userId ? 'You' : inp.user_name}
                              </span>
                              <span className="text-[10px] text-stone-600 ml-auto">{formatTime(inp.created_at)}</span>
                            </div>
                            <p className="text-stone-300 text-xs pl-6">{inp.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Live Stream Tab - All inputs chronologically */}
          <TabsContent value="stream">
            <Card className="bg-stone-900/50 border-stone-800">
              <CardHeader>
                <CardTitle className="text-amber-300 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Live Practice Stream
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  {allInputs.length === 0 ? (
                    <div className="text-center py-12 text-stone-500">
                      <LotusIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No inputs yet. Be the first to share!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {allInputs.map((inp) => (
                        <div 
                          key={inp._id}
                          className={`p-4 rounded-lg ${
                            inp.user_id === userId 
                              ? 'bg-amber-500/10 border border-amber-500/20 ml-8' 
                              : 'bg-stone-800/50 border border-stone-700 mr-8'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-6 h-6 rounded-full ${getUserColor(inp.user_id)} flex items-center justify-center text-white text-xs font-medium`}>
                              {inp.user_name.charAt(0).toUpperCase()}
                            </div>
                            <span className={`text-sm font-medium ${inp.user_id === userId ? 'text-amber-400' : 'text-teal-400'}`}>
                              {inp.user_id === userId ? 'You' : inp.user_name}
                            </span>
                            <Badge variant="outline" className="text-[10px] border-stone-600 text-stone-500 ml-2">
                              {inp.field_label}
                            </Badge>
                            <span className="text-xs text-stone-600 ml-auto">{formatTime(inp.created_at)}</span>
                          </div>
                          <p className="text-stone-200 pl-8">{inp.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compiled By User Tab */}
          <TabsContent value="compiled">
            <Card className="bg-stone-900/50 border-stone-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-amber-300 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Compiled by User
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchByUserView}
                    className="border-stone-700 text-stone-400"
                    data-testid="refresh-user-view-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!userView ? (
                  <div className="text-center py-12">
                    <Button onClick={fetchByUserView} className="bg-amber-600 hover:bg-amber-500">
                      Load User Summaries
                    </Button>
                  </div>
                ) : Object.keys(userView.user_data || {}).length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No responses yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(userView.user_data).map(([uid, userData]) => (
                      <div 
                        key={uid}
                        className={`border rounded-lg ${
                          uid === userId ? 'border-amber-500/30 bg-amber-500/5' : 'border-stone-700 bg-stone-800/30'
                        }`}
                      >
                        <button
                          onClick={() => toggleUserExpanded(uid)}
                          className="w-full p-4 flex items-center justify-between text-left"
                          data-testid={`user-${uid}-toggle`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full ${getUserColor(uid)} flex items-center justify-center text-white font-medium`}>
                              {userData.user_name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <span className={`font-medium ${uid === userId ? 'text-amber-400' : 'text-white'}`}>
                                {uid === userId ? 'You' : userData.user_name}
                              </span>
                              <p className="text-xs text-stone-500">
                                {Object.values(userData.fields).filter(f => f.content).length} / {Object.keys(userData.fields).length} fields completed
                              </p>
                            </div>
                          </div>
                          {expandedUsers[uid] ? (
                            <ChevronUp className="w-5 h-5 text-stone-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-stone-500" />
                          )}
                        </button>
                        
                        {expandedUsers[uid] && (
                          <div className="px-4 pb-4 space-y-3">
                            {Object.entries(userData.fields).map(([fieldName, fieldData]) => (
                              <div key={fieldName} className="pl-11">
                                <p className="text-xs text-stone-500 mb-1">{fieldData.label}</p>
                                {fieldData.content ? (
                                  <p className="text-sm text-stone-200 bg-stone-800/50 p-2 rounded">
                                    {fieldData.content}
                                  </p>
                                ) : (
                                  <p className="text-sm text-stone-600 italic">Not yet answered</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <PremiumFooter variant="dark" />
    </div>
  );
}
